import { useMutation } from "@tanstack/react-query";

import type { SendDocsFormData } from "~/features/quote/components/shipment-details/components/tender-modal/hooks/use-tender-modal.tsx";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";
import type { ApiResult } from "~/shared/types/api-types.ts";
import { getIsInternal } from "~/shared/utils/get-deployment-type.ts";

import type {
	BulkTenderDocumentsEmailRequest,
	BulkTenderDocumentsEmailResponse,
} from "../schemas/bulk-tender-document-email-schema.ts";
import {
	bulkTenderDocumentsEmailRequestSchema,
	bulkTenderDocumentsEmailResponseSchema,
} from "../schemas/bulk-tender-document-email-schema.ts";
import type { BulkTenderDocumentFormData } from "../schemas/bulk-tender-document-schema.ts";

const INVALID_EMAIL_REQUEST_MESSAGE =
	"Add at least one valid shipment before sending bulk tender documents.";
const BULK_TRACKING_LINK_TEXT =
	"\n\nTracking links for each shipment will be included in this email.";

const parseRecipients = (value: string): string[] =>
	value
		.split(",")
		.map((recipient) => recipient.trim())
		.filter((recipient) => recipient.length > 0);

export const useBulkTenderDocumentsEmail = () =>
	useMutation<
		ApiResult<BulkTenderDocumentsEmailResponse>,
		Error,
		BulkTenderDocumentsEmailRequest
	>({
		mutationFn: async (request) => {
			const parsedRequest =
				bulkTenderDocumentsEmailRequestSchema.safeParse(request);
			if (!parsedRequest.success) {
				return {
					success: false,
					error: INVALID_EMAIL_REQUEST_MESSAGE,
					details: parsedRequest.error,
				} as const;
			}

			const route = getIsInternal()
				? apiRoutes.BULKTENDERDOCUMENTSSEND
				: apiRoutes.BULKTENDERDOCUMENTSCUSTOMERSEND;
			return await postAndValidate(
				route,
				parsedRequest.data,
				bulkTenderDocumentsEmailResponseSchema,
			);
		},
	});

type UseBulkTenderDocumentsEmailComposerOptions = {
	quoteIds: number[];
	formData: BulkTenderDocumentFormData;
	isInternal: boolean;
	email: string | null | undefined;
	onSent?: (response: BulkTenderDocumentsEmailResponse) => void;
	onError?: (error: Error) => void;
};

export const useBulkTenderDocumentsEmailComposer = ({
	quoteIds,
	formData,
	isInternal,
	email,
	onSent,
	onError,
}: UseBulkTenderDocumentsEmailComposerOptions) => {
	const mutation = useBulkTenderDocumentsEmail();

	const getInitialFormValues = () => ({
		to: email ?? "",
		subject: "Your Bulk Tender Documents are Ready",
		message: "Your bulk tender documents are attached.",
	});

	const handleSubmit = (
		formDataFromComposer: SendDocsFormData,
		onSuccess?: () => void,
		onFailure?: (error: Error) => void,
	) => {
		const request: BulkTenderDocumentsEmailRequest = {
			quoteIds,
			formData,
			subject: isInternal ? formDataFromComposer.subject : undefined,
			messageBody: isInternal ? formDataFromComposer.message : undefined,
			recipients: parseRecipients(formDataFromComposer.to),
			includeTrackingLink: isInternal
				? true
				: (formDataFromComposer.includeTrackingLink ?? false),
		};

		mutation.mutate(request, {
			onSuccess: (result) => {
				if (result.success) {
					onSent?.(result.data);
					onSuccess?.();
					return;
				}
				const error = new Error(result.error);
				onError?.(error);
				onFailure?.(error);
			},
			onError: (error) => {
				onError?.(error);
				onFailure?.(error);
			},
		});
	};

	return {
		getInitialFormValues,
		getTrackingLinkText: () => BULK_TRACKING_LINK_TEXT,
		handleSubmit,
		cancelEmail: () => mutation.reset(),
		isLoading: mutation.isPending,
		isSuccess: mutation.isSuccess,
		isError: mutation.isError,
	};
};
